import { create } from 'zustand';
import apiClient from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';

// Types matching backend structure
export interface MediaVariant {
    type: 'thumbnail' | 'standard' | 'hd' | 'original';
    url: string;
    width?: number;
    height?: number;
    size?: number;
}

export interface MediaUrls {
    thumbnails: string[];
    variants: MediaVariant[];
    primary: string;
    optimized?: string; // For videos: processed version
}

export interface Post {
    id: string;
    user_id: string;
    content: string;
    media_keys: string[];
    media_type: 'image' | 'video' | 'carousel' | 'text';
    thumbnail_keys: string[];
    media_urls?: MediaUrls;
    likes_count: number;
    comments_count: number;
    view_count: number;
    created_at: string;
    username: string;
    avatar_key?: string;
    is_liked: boolean;
    processing_state?: 'pending' | 'processing' | 'completed' | 'failed';
    processed_video_key?: string;

    // Frontend only
    cursor?: string;
}

export interface Comment {
    id: string;
    user_id: string;
    post_id: string;
    content: string;
    created_at: string;
    user: {
        username: string;
        full_name?: string;
        avatar_url?: string;
        verified?: boolean;
    };
    likes?: number;
    replies?: Comment[];

    // Frontend formatted
    text?: string;
    timestamp?: string;
}

export interface Hashtag {
    tag: string;
    usage_count: number;
}

export interface ExplorePost {
    id: string;
    media_keys: string[];
    media_type: 'image' | 'video';
    thumbnail_keys: string[];
    likes_count: number;
    view_count: number;
    username: string;
    avatar_key?: string;
}

export interface Pagination {
    page?: number;
    limit: number;
    has_more: boolean;
    next_cursor?: string;
    total?: number;
}

export interface FeedResponse {
    posts: Post[];
    pagination: Pagination;
    meta?: {
        cache_hint?: string;
        generated_at?: string;
        preload_urls?: string[];
        performance_tips?: Record<string, any>;
    };
}

interface FeedState {
    // Data
    feedPosts: Post[];
    explorePosts: ExplorePost[];
    trendingHashtags: Hashtag[];
    userPosts: Record<string, Post[]>; // userId -> posts
    postComments: Record<string, Comment[]>; // postId -> comments
    userLikes: Set<string>; // Set of liked post IDs

    // Loading states
    feedLoading: boolean;
    feedLoadingMore: boolean;
    exploreLoading: boolean;
    trendingLoading: boolean;
    userPostsLoading: Record<string, boolean>; // userId -> loading state
    commentsLoading: Record<string, boolean>; // postId -> loading state
    likeLoading: Record<string, boolean>; // postId -> loading state
    createPostLoading: boolean;
    addCommentLoading: Record<string, boolean>; // postId -> loading state

    // Error states
    feedError: string | null;
    exploreError: string | null;
    trendingError: string | null;
    userPostsError: Record<string, string | null>; // userId -> error
    commentsError: Record<string, string | null>; // postId -> error
    createPostError: string | null;
    likeError: string | null;
    addCommentError: Record<string, string | null>; // postId -> error

    // Pagination
    feedPagination: Pagination;
    explorePagination: Pagination;
    userPostsPagination: Record<string, Pagination>; // userId -> pagination

    // Actions
    // Feed
    fetchFeed: (params?: {
        page?: number;
        limit?: number;
        cursor?: string;
        refresh?: boolean;
    }) => Promise<void>;
    loadMoreFeed: () => Promise<void>;

    // Explore
    fetchExplorePosts: (params?: {
        page?: number;
        limit?: number;
    }) => Promise<void>;

    // Trending
    fetchTrendingHashtags: () => Promise<void>;

    // User posts
    fetchUserPosts: (userId: string, params?: {
        page?: number;
        limit?: number;
    }) => Promise<void>;
    loadMoreUserPosts: (userId: string) => Promise<void>;

    // Comments
    fetchComments: (postId: string, params?: {
        page?: number;
        limit?: number;
    }) => Promise<void>;
    loadMoreComments: (postId: string) => Promise<void>;

    // Interactions
    createPost: (data: {
        content: string;
        media_keys?: string[];
        media_type?: 'image' | 'video' | 'carousel' | 'text';
        thumbnail_keys?: string[];
    }) => Promise<Post>;
    toggleLike: (postId: string) => Promise<{
        success: boolean;
        liked: boolean;
        new_likes_count: number;
    }>;
    addComment: (postId: string, content: string) => Promise<Comment>;
    saveMedia: (data: {
        key: string;
        type: 'image' | 'video';
        file_size: number;
        mime_type: string;
    }) => Promise<any>;

    // Utility
    clearErrors: () => void;
    resetFeed: () => void;
    resetUserPosts: (userId: string) => void;
    resetComments: (postId: string) => void;

    // Helper to check if post is liked
    isPostLiked: (postId: string) => boolean;
}

const CDN_BASE = 'https://media.majengo.co.ke';

const resolveKey = (key: string): string => {
    if (!key) return '';
    if (key.startsWith('http://') || key.startsWith('https://')) return key;
    return `${CDN_BASE}/${key.replace(/^\//, '')}`;
};

// Helper function to generate media URLs
const generateMediaUrls = (
    mediaKeys: string[],
    mediaType: string,
    thumbnailKeys?: string[]
): MediaUrls => {
    if (!mediaKeys || mediaKeys.length === 0) {
        return { thumbnails: [], variants: [], primary: '' };
    }

    const primaryUrl = resolveKey(mediaKeys[0]);
    const thumbnails = (thumbnailKeys || []).map(resolveKey).filter(Boolean);
    const variants: MediaVariant[] = mediaKeys.map((key, index) => ({
        type: (mediaType === 'video' && index === 0 ? 'hd' : index === 0 ? 'original' : 'standard') as MediaVariant['type'],
        url: resolveKey(key),
    }));

    return {
        thumbnails: thumbnails.length > 0 ? thumbnails : [primaryUrl],
        variants,
        primary: primaryUrl,
        ...(mediaType === 'video' && thumbnails[0] && { optimized: thumbnails[0] })
    };
};

export const useFeedStore = create<FeedState>((set, get) => ({
    // Initial state
    feedPosts: [],
    explorePosts: [],
    trendingHashtags: [],
    userPosts: {},
    postComments: {},
    userLikes: new Set(),

    feedLoading: false,
    feedLoadingMore: false,
    exploreLoading: false,
    trendingLoading: false,
    userPostsLoading: {},
    commentsLoading: {},
    likeLoading: {},
    createPostLoading: false,
    addCommentLoading: {},

    feedError: null,
    exploreError: null,
    trendingError: null,
    userPostsError: {},
    commentsError: {},
    createPostError: null,
    likeError: null,
    addCommentError: {},

    feedPagination: { limit: 20, has_more: false },
    explorePagination: { limit: 30, has_more: false },
    userPostsPagination: {},

    // ========== Feed Actions ==========
    fetchFeed: async (params = {}) => {
        const { page = 1, limit = 20, cursor, refresh = false } = params;

        if (refresh) {
            set({ feedLoading: true, feedError: null });
        } else if (page > 1) {
            set({ feedLoadingMore: true });
        } else {
            set({ feedLoading: true, feedError: null });
        }

        try {
            const queryParams: Record<string, any> = { limit };
            if (cursor) queryParams.cursor = cursor;
            if (page > 1) queryParams.page = page;

            const response = await apiClient.get(API_ENDPOINTS.FEED_GET, {
                params: queryParams
            });

            const feedData: FeedResponse = response.data;

            // Generate media URLs for posts
            const postsWithMedia = feedData.posts.map(post => ({
                ...post,
                media_urls: generateMediaUrls(
                    post.media_keys,
                    post.media_type,
                    post.thumbnail_keys
                )
            }));

            set((state) => ({
                feedPosts: refresh || page === 1
                    ? postsWithMedia
                    : [...state.feedPosts, ...postsWithMedia],
                feedPagination: feedData.pagination,
                feedLoading: false,
                feedLoadingMore: false,
                feedError: null,

                // Update user likes
                userLikes: new Set([
                    ...Array.from(state.userLikes),
                    ...postsWithMedia
                        .filter(post => post.is_liked)
                        .map(post => post.id)
                ])
            }));

        } catch (error: any) {
            console.error('Failed to fetch feed:', error);
            set({
                feedLoading: false,
                feedLoadingMore: false,
                feedError: error.response?.data?.error || 'Failed to load feed'
            });
        }
    },

    loadMoreFeed: async () => {
        const state = get();
        if (state.feedLoadingMore || !state.feedPagination.has_more) return;

        const cursor = state.feedPosts[state.feedPosts.length - 1]?.cursor;
        if (!cursor) return;

        await get().fetchFeed({ cursor });
    },

    // ========== Explore Posts ==========
    fetchExplorePosts: async (params = {}) => {
        const { page = 1, limit = 30 } = params;
        set({ exploreLoading: true, exploreError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.FEED_EXPLORE_POSTS, {
                params: { page, limit }
            });

            const { posts } = response.data;

            // Generate media URLs for explore posts
            const explorePostsWithMedia = posts.map((post: any) => ({
                ...post,
                media_urls: generateMediaUrls(
                    post.media_keys,
                    post.media_type,
                    post.thumbnail_keys
                )
            }));

            set({
                explorePosts: explorePostsWithMedia,
                exploreLoading: false,
                exploreError: null
            });

        } catch (error: any) {
            console.error('Failed to fetch explore posts:', error);
            set({
                exploreLoading: false,
                exploreError: error.response?.data?.error || 'Failed to load explore posts'
            });
        }
    },

    // ========== Trending Hashtags ==========
    fetchTrendingHashtags: async () => {
        set({ trendingLoading: true, trendingError: null });

        try {
            const response = await apiClient.get(API_ENDPOINTS.FEED_TRENDING_HASHTAGS);

            set({
                trendingHashtags: response.data.hashtags || [],
                trendingLoading: false,
                trendingError: null
            });

        } catch (error: any) {
            console.error('Failed to fetch trending hashtags:', error);
            set({
                trendingLoading: false,
                trendingError: error.response?.data?.error || 'Failed to load trending hashtags'
            });
        }
    },

    // ========== User Posts ==========
    fetchUserPosts: async (userId: string, params = {}) => {
        const { page = 1, limit = 20 } = params;

        set((state) => ({
            userPostsLoading: { ...state.userPostsLoading, [userId]: true },
            userPostsError: { ...state.userPostsError, [userId]: null }
        }));

        try {
            const response = await apiClient.get(API_ENDPOINTS.FEED_USER_POSTS(userId), {
                params: { page, limit }
            });

            const { posts, pagination } = response.data;

            // Generate media URLs for user posts
            const postsWithMedia = posts.map((post: any) => ({
                ...post,
                media_urls: generateMediaUrls(
                    post.media_keys,
                    post.media_type,
                    post.thumbnail_keys
                )
            }));

            set((state) => ({
                userPosts: {
                    ...state.userPosts,
                    [userId]: page === 1
                        ? postsWithMedia
                        : [...(state.userPosts[userId] || []), ...postsWithMedia]
                },
                userPostsPagination: {
                    ...state.userPostsPagination,
                    [userId]: pagination
                },
                userPostsLoading: { ...state.userPostsLoading, [userId]: false },
                userPostsError: { ...state.userPostsError, [userId]: null },

                // Update user likes
                userLikes: new Set([
                    ...Array.from(state.userLikes),
                    ...postsWithMedia
                        .filter((post: any) => post.is_liked)
                        .map((post: any) => post.id)
                ])
            }));

        } catch (error: any) {
            console.error(`Failed to fetch user posts for ${userId}:`, error);
            set((state) => ({
                userPostsLoading: { ...state.userPostsLoading, [userId]: false },
                userPostsError: {
                    ...state.userPostsError,
                    [userId]: error.response?.data?.error || 'Failed to load user posts'
                }
            }));
        }
    },

    loadMoreUserPosts: async (userId: string) => {
        const state = get();
        const userPagination = state.userPostsPagination[userId];

        if (!userPagination?.has_more || state.userPostsLoading[userId]) return;

        const currentPage = Math.ceil((state.userPosts[userId]?.length || 0) / 20) + 1;
        await get().fetchUserPosts(userId, { page: currentPage });
    },

    // ========== Comments ==========
    fetchComments: async (postId: string, params = {}) => {
        const { page = 1, limit = 50 } = params;

        set((state) => ({
            commentsLoading: { ...state.commentsLoading, [postId]: true },
            commentsError: { ...state.commentsError, [postId]: null }
        }));

        try {
            const response = await apiClient.get(API_ENDPOINTS.FEED_GET_COMMENTS(postId), {
                params: { page, limit }
            });

            const { comments } = response.data;

            // Format comments for display
            const formattedComments = comments.map((comment: any) => ({
                ...comment,
                text: comment.content,
                timestamp: formatCommentTime(comment.created_at)
            }));

            set((state) => ({
                postComments: {
                    ...state.postComments,
                    [postId]: page === 1
                        ? formattedComments
                        : [...(state.postComments[postId] || []), ...formattedComments]
                },
                commentsLoading: { ...state.commentsLoading, [postId]: false },
                commentsError: { ...state.commentsError, [postId]: null }
            }));

        } catch (error: any) {
            console.error(`Failed to fetch comments for post ${postId}:`, error);
            set((state) => ({
                commentsLoading: { ...state.commentsLoading, [postId]: false },
                commentsError: {
                    ...state.commentsError,
                    [postId]: error.response?.data?.error || 'Failed to load comments'
                }
            }));
        }
    },

    loadMoreComments: async (postId: string) => {
        const state = get();
        const comments = state.postComments[postId] || [];

        if (state.commentsLoading[postId] || comments.length % 50 !== 0) return;

        const nextPage = Math.ceil(comments.length / 50) + 1;
        await get().fetchComments(postId, { page: nextPage });
    },

    // ========== Create Post ==========
    createPost: async (data) => {
        set({ createPostLoading: true, createPostError: null });

        try {
            const response = await apiClient.post(API_ENDPOINTS.FEED_CREATE_POST, data);
            const post = response.data;

            // Generate media URLs for the new post
            const postWithMedia = {
                ...post,
                media_urls: generateMediaUrls(
                    post.media_keys,
                    post.media_type,
                    post.thumbnail_keys
                )
            };

            // Add to feed (at the beginning)
            set((state) => ({
                feedPosts: [postWithMedia, ...state.feedPosts],
                createPostLoading: false,
                createPostError: null
            }));

            return postWithMedia;

        } catch (error: any) {
            console.error('Failed to create post:', error);
            set({
                createPostLoading: false,
                createPostError: error.response?.data?.error || 'Failed to create post'
            });
            throw error;
        }
    },

    // ========== Toggle Like ==========
    toggleLike: async (postId: string) => {
        const state = get();

        // Optimistic update
        const isCurrentlyLiked = state.userLikes.has(postId);
        const currentLikes = state.feedPosts.find(p => p.id === postId)?.likes_count || 0;

        set((state) => ({
            likeLoading: { ...state.likeLoading, [postId]: true },
            likeError: null,
            userLikes: new Set(isCurrentlyLiked
                ? Array.from(state.userLikes).filter(id => id !== postId)
                : [...Array.from(state.userLikes), postId]
            ),
            feedPosts: state.feedPosts.map(post =>
                post.id === postId
                    ? {
                        ...post,
                        likes_count: isCurrentlyLiked ? post.likes_count - 1 : post.likes_count + 1,
                        is_liked: !isCurrentlyLiked
                    }
                    : post
            )
        }));

        try {
            const response = await apiClient.post(API_ENDPOINTS.FEED_TOGGLE_LIKE(postId));

            set({
                likeLoading: { ...state.likeLoading, [postId]: false },
                likeError: null
            });

            return response.data;

        } catch (error: any) {
            console.error(`Failed to toggle like for post ${postId}:`, error);

            // Revert optimistic update on error
            set((state) => ({
                likeLoading: { ...state.likeLoading, [postId]: false },
                likeError: error.response?.data?.error || 'Failed to toggle like',
                userLikes: new Set(!isCurrentlyLiked
                    ? Array.from(state.userLikes).filter(id => id !== postId)
                    : [...Array.from(state.userLikes), postId]
                ),
                feedPosts: state.feedPosts.map(post =>
                    post.id === postId
                        ? {
                            ...post,
                            likes_count: currentLikes,
                            is_liked: isCurrentlyLiked
                        }
                        : post
                )
            }));

            throw error;
        }
    },

    // ========== Add Comment ==========
    addComment: async (postId: string, content: string) => {
        set((state) => ({
            addCommentLoading: { ...state.addCommentLoading, [postId]: true },
            addCommentError: { ...state.addCommentError, [postId]: null }
        }));

        try {
            const response = await apiClient.post(API_ENDPOINTS.FEED_ADD_COMMENT(postId), { content });
            const comment = response.data;

            // Format comment for display
            const formattedComment = {
                ...comment,
                text: comment.content,
                timestamp: 'now'
            };

            set((state) => ({
                postComments: {
                    ...state.postComments,
                    [postId]: [formattedComment, ...(state.postComments[postId] || [])]
                },
                addCommentLoading: { ...state.addCommentLoading, [postId]: false },
                addCommentError: { ...state.addCommentError, [postId]: null },

                // Update comment count in feed
                feedPosts: state.feedPosts.map(post =>
                    post.id === postId
                        ? { ...post, comments_count: post.comments_count + 1 }
                        : post
                )
            }));

            return formattedComment;

        } catch (error: any) {
            console.error(`Failed to add comment to post ${postId}:`, error);
            set((state) => ({
                addCommentLoading: { ...state.addCommentLoading, [postId]: false },
                addCommentError: {
                    ...state.addCommentError,
                    [postId]: error.response?.data?.error || 'Failed to add comment'
                }
            }));
            throw error;
        }
    },

    // ========== Save Media ==========
    saveMedia: async (data) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.FEED_SAVE_MEDIA, data);
            return response.data;
        } catch (error: any) {
            console.error('Failed to save media:', error);
            throw error;
        }
    },

    // ========== Utility Functions ==========
    clearErrors: () => {
        set({
            feedError: null,
            exploreError: null,
            trendingError: null,
            createPostError: null,
            likeError: null
        });
    },

    resetFeed: () => {
        set({
            feedPosts: [],
            feedPagination: { limit: 20, has_more: false },
            feedError: null
        });
    },

    resetUserPosts: (userId: string) => {
        set((state) => ({
            userPosts: { ...state.userPosts, [userId]: [] },
            userPostsPagination: {
                ...state.userPostsPagination,
                [userId]: { limit: 20, has_more: false }
            },
            userPostsError: { ...state.userPostsError, [userId]: null }
        }));
    },

    resetComments: (postId: string) => {
        set((state) => ({
            postComments: { ...state.postComments, [postId]: [] },
            commentsError: { ...state.commentsError, [postId]: null }
        }));
    },

    isPostLiked: (postId: string) => {
        return get().userLikes.has(postId);
    }
}));

// Helper function to format comment timestamps
const formatCommentTime = (timestamp: string): string => {
    const now = new Date();
    const commentTime = new Date(timestamp);
    const diffMs = now.getTime() - commentTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
};